import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import AuthShell from "./AuthShell";
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // Supabase sends recovery tokens as a hash fragment in the URL.
    // We must listen to the PASSWORD_RECOVERY auth event which fires
    // after Supabase SDK automatically exchanges the hash token for a session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        // Session is now active - user can set a new password
        setHasSession(true);
        setCheckingSession(false);
      } else if (event === "SIGNED_IN" && session) {
        // Already logged in via recovery link (session established)
        setHasSession(true);
        setCheckingSession(false);
      }
    });

    // Also check if there's already a valid session from the hash exchange
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setHasSession(true);
      } else {
        // Give 3 seconds for the PASSWORD_RECOVERY event to fire
        // before showing the error (hash exchange takes a moment)
        setTimeout(() => {
          setCheckingSession((prev) => {
            if (prev) {
              // Still checking after timeout — no valid token found
              setErr("Sessão expirada ou link de recuperação inválido. Por favor, solicite um novo link.");
              return false;
            }
            return prev;
          });
        }, 3000);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const strengthCheck = (pwd) => {
    if (pwd.length === 0) return null;
    if (pwd.length < 6) return { level: "fraca", color: "bg-red-400", textColor: "text-red-600" };
    if (pwd.length < 10) return { level: "média", color: "bg-amber-400", textColor: "text-amber-600" };
    return { level: "forte", color: "bg-emerald-500", textColor: "text-emerald-600" };
  };

  const strength = strengthCheck(password);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (password.length < 6) {
      setErr("A senha deve ter no mínimo 6 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setErr("As senhas não coincidem.");
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Sign out to clean up session and force user to log in with new credentials
      await supabase.auth.signOut();
      setSuccess(true);
    } catch (e2) {
      setErr(e2.message || "Erro ao tentar atualizar a senha.");
    } finally {
      setBusy(false);
    }
  };

  if (checkingSession) {
    return (
      <AuthShell title="Validando link" subtitle="Por favor, aguarde enquanto verificamos seu token de acesso.">
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <div className="w-10 h-10 rounded-full border-4 border-[hsl(var(--brand-light))] border-t-[hsl(var(--brand))] animate-spin" />
          <p className="text-sm text-[hsl(var(--text-tertiary))]">Verificando sessão de recuperação...</p>
        </div>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell
        title="Senha redefinida!"
        subtitle="Sua senha foi atualizada com sucesso."
        footer={
          <Link to="/login" className="ds-btn-primary w-full h-11 flex items-center justify-center gap-2 font-bold text-center">
            Fazer Login
          </Link>
        }
      >
        <div className="ds-card p-6 flex flex-col items-center text-center bg-emerald-500/10 border-emerald-500/20">
          <div className="w-16 h-16 rounded-full bg-white/50 flex items-center justify-center mb-4 shadow-sm">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit',sans-serif" }}>
            Senha Alterada
          </h3>
          <p className="text-sm text-[hsl(var(--text-secondary))]">
            Agora você já pode fazer login na plataforma utilizando sua nova senha.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Nova senha"
      subtitle="Defina sua nova senha de acesso abaixo."
      footer={
        <div className="text-sm text-[hsl(var(--text-secondary))] text-center">
          Deseja cancelar?{" "}
          <Link to="/login" className="ds-link font-bold text-[hsl(var(--brand))]">Voltar para o Login</Link>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {err && (
          <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-xl px-4 py-3 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{err}</span>
          </div>
        )}

        {hasSession ? (
          <>
            {/* New password field */}
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Nova Senha</span>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  autoFocus
                  className="ds-input pl-11 pr-11 h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]">
                  <Lock className="w-4 h-4" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] transition-colors"
                  aria-label="Mostrar senha"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password strength bar */}
              {strength && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-[hsl(var(--bg-hover))] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                      style={{ width: strength.level === "fraca" ? "33%" : strength.level === "média" ? "66%" : "100%" }}
                    />
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${strength.textColor}`}>
                    {strength.level}
                  </span>
                </div>
              )}
            </label>

            {/* Confirm password field */}
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Confirmar Nova Senha</span>
              <div className="relative">
                <input
                  type={showConfirmPwd ? "text" : "password"}
                  required
                  className="ds-input pl-11 pr-11 h-11"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                />
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]">
                  <Lock className="w-4 h-4" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] transition-colors"
                  aria-label="Mostrar confirmação de senha"
                >
                  {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Match indicator */}
              {confirmPassword.length > 0 && (
                <p className={`text-[10px] font-bold mt-1.5 ${password === confirmPassword ? "text-emerald-600" : "text-red-500"}`}>
                  {password === confirmPassword ? "✓ Senhas coincidem" : "✗ Senhas não coincidem"}
                </p>
              )}
            </label>

            <button
              type="submit"
              disabled={busy || !password || !confirmPassword}
              className="ds-btn-primary w-full h-11 font-bold"
            >
              {busy ? "Atualizando..." : "Redefinir Senha"}
            </button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-xl px-4 py-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Sessão expirada ou link inválido. Solicite um novo link de recuperação.</span>
            </div>
            <Link
              to="/forgot-password"
              className="ds-btn-primary w-full h-11 flex items-center justify-center gap-2 font-bold text-center"
            >
              Solicitar novo link
            </Link>
          </div>
        )}
      </form>
    </AuthShell>
  );
}
